'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import {
  fetchNews,
  categoryColour,
  newsDate,
  readTime,
  type NewsPost,
} from '@/lib/news';

/**
 * News & Updates.
 *
 * Reads posts the FarmTradeDirect team writes in Admin > News. It previously held
 * eight invented articles — see the news_posts migration for why they came down
 * rather than being left in as placeholders.
 */
export default function NewsUpdatesScreen() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('All');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchNews(createClient())
      .then((rows) => { if (live) setPosts(rows); })
      .catch((err) => { if (live) setError(err?.message ?? 'Could not load the news.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean) as string[])).sort()],
    [posts]
  );

  const shown = category === 'All' ? posts : posts.filter((p) => p.category === category);

  return (
    <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-extrabold text-foreground">News &amp; Market Reports</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
          Market reports, auction results and industry news from the FarmTradeDirect team.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6" role="alert">
          <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="card h-56 animate-pulse bg-muted/40" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="NewspaperIcon" size={26} className="text-muted-foreground" />
          </div>
          <h3 className="font-bold text-foreground mb-2">No articles published yet</h3>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Market reports and auction results will be published here. Nothing is shown until
            it is written — a price report is only worth reading if the figures are real.
          </p>
        </div>
      ) : (
        <>
          {categories.length > 2 && (
            <div className="flex gap-2 flex-wrap mb-6">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    category === c
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-primary/40'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {shown.map((post) => {
              const open = openId === post.id;
              return (
                <article key={post.id} className="card flex flex-col gap-3">
                  {post.image_url && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={post.image_url} alt={post.title} className="w-full h-40 object-cover rounded-lg" />
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {post.category && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColour(post.category)}`}>
                        {post.category}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{newsDate(post)}</span>
                    <span className="text-xs text-muted-foreground">· {readTime(post)}</span>
                  </div>

                  <h2 className="font-bold text-foreground text-sm leading-snug">{post.title}</h2>

                  {post.excerpt && (
                    <p className="text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
                  )}

                  {open && post.body && (
                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">
                      {post.body}
                    </p>
                  )}

                  {/* Where the figures came from. Shown prominently on purpose:
                      it is the difference between a market report and a rumour. */}
                  {post.source_name && (
                    <p className="text-xs text-muted-foreground">
                      Source:{' '}
                      {post.source_url ? (
                        <a
                          href={post.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {post.source_name}
                        </a>
                      ) : (
                        post.source_name
                      )}
                    </p>
                  )}

                  <div className="mt-auto pt-2 flex items-center justify-between gap-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {post.author || 'FarmTradeDirect'}
                    </span>
                    {post.body && (
                      <button
                        className="text-sm font-semibold text-primary hover:underline"
                        onClick={() => setOpenId(open ? null : post.id)}
                      >
                        {open ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </main>
  );
}
